import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { getCookie, parseConsent, CONSENT_VERSION } from '../components/auth/PrivacyBanner';

const AuthContext = createContext(null);

// Fetches the app profile (status + waitlist position) for the given auth user.
// Retries briefly because the handle_new_user trigger runs after sign-in and
// the SIGNED_IN event can fire a few ms before the row is visible.
const fetchProfile = async (userId, attempts = 3) => {
  for (let i = 0; i < attempts; i++) {
    const { data, error } = await supabase
      .from('profiles')
      .select('status, waitlist_position')
      .eq('id', userId)
      .maybeSingle();
    if (!error && data) return data;
    if (i < attempts - 1) {
      await new Promise(r => setTimeout(r, 300));
    }
  }
  return null;
};

// Fire-and-forget persist of the privacy consent cookie under the user's JWT.
// Uses an AbortController with a short timeout so a slow/hung request can
// never block the auth bootstrap or gate the UI.
const CONSENT_PERSIST_TIMEOUT_MS = 5000;
const persistConsentIfPresent = (accessToken) => {
  if (!accessToken) return;
  try {
    const raw = getCookie('privacy_consent');
    const c = parseConsent(raw);
    if (!c || c.version !== CONSENT_VERSION) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONSENT_PERSIST_TIMEOUT_MS);

    fetch('/api/consent/persist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        version: CONSENT_VERSION,
        necessary: !!c.necessary,
        analytics: !!c.analytics
      }),
      signal: controller.signal
    })
      .catch(() => {})
      .finally(() => clearTimeout(timeoutId));
  } catch (_) {}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrateUser = async (sessionUser) => {
    const profile = await fetchProfile(sessionUser.id);
    return {
      id: sessionUser.id,
      email: sessionUser.email,
      status: profile?.status ?? 'waitlisted',
      waitlistPosition: profile?.waitlist_position ?? null,
    };
  };

  useEffect(() => {
    let cancelled = false;

    // Apply a session to React state. Used by both the onAuthStateChange
    // listener and the initial getSession fallback.
    const applySession = async (session, { persistConsent = false } = {}) => {
      if (cancelled) return;
      if (!session?.user) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const hydrated = await hydrateUser(session.user);
        if (cancelled) return;
        setUser(hydrated);
      } catch (err) {
        console.error('Auth hydrate error:', err);
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
      if (persistConsent) {
        persistConsentIfPresent(session.access_token);
      }
    };

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Handle every event that conveys session state so we never leave the
        // app stuck in loading. INITIAL_SESSION fires on every page load and
        // is what drives the "return to site" path.
        if (event === 'SIGNED_OUT') {
          if (!cancelled) {
            setUser(null);
            setLoading(false);
          }
          return;
        }

        const shouldPersistConsent = event === 'SIGNED_IN';
        applySession(session, { persistConsent: shouldPersistConsent });
      }
    );

    // Fallback: if for any reason onAuthStateChange never emits (shouldn't
    // happen, but guards against a wedged subscription), resolve loading
    // from getSession() directly. This is a belt-and-braces safeguard.
    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error) {
          console.error('Auth check error:', error);
          setUser(null);
          setLoading(false);
          return;
        }
        // Only drive state here if the listener hasn't already flipped
        // loading to false — applySession itself is idempotent.
        applySession(data?.session ?? null);
      } catch (err) {
        console.error('Auth check error:', err);
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      authListener?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        }
      });
      
      if (error) {
        console.error('Supabase auth error:', error);
        throw error;
      }
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Supabase logout error:', error);
      }
      setUser(null);
      window.location.href = '/login';
    } catch (err) {
      console.error('Logout error:', err);
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
